import React, { useState, useEffect } from 'react';

interface QualityIssue {
  id: string;
  type: 'error' | 'warning' | 'info';
  severity: 'high' | 'medium' | 'low';
  message: string;
  file: string;
  line: number;
  rule: string;
  category: string;
  timestamp: string;
}

interface QualityMetrics {
  project: string;
  totalIssues: number;
  errors: number;
  warnings: number;
  info: number;
  maintainabilityIndex: number;
  technicalDebt: number;
  codeSmells: number;
  duplications: number;
  lastAnalysis: string;
}

const QualityComponent: React.FC = () => {
  const [qualityMetrics, setQualityMetrics] = useState<QualityMetrics[]>([]);
  const [issues, setIssues] = useState<QualityIssue[]>([]);
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchQualityData();
  }, []);

  const fetchQualityData = async () => {
    setIsLoading(true);
    try {
      const mockMetrics: QualityMetrics[] = [
        {
          project: 'admin-dashboard',
          totalIssues: 15,
          errors: 2,
          warnings: 8,
          info: 5,
          maintainabilityIndex: 85,
          technicalDebt: 2.5,
          codeSmells: 12,
          duplications: 3,
          lastAnalysis: new Date(Date.now() - 1800000).toISOString()
        },
        {
          project: 'landing-page',
          totalIssues: 8,
          errors: 0,
          warnings: 5,
          info: 3,
          maintainabilityIndex: 92,
          technicalDebt: 1.2,
          codeSmells: 6,
          duplications: 1,
          lastAnalysis: new Date(Date.now() - 3600000).toISOString()
        },
        {
          project: 'backend-api',
          totalIssues: 22,
          errors: 3,
          warnings: 12,
          info: 7,
          maintainabilityIndex: 78,
          technicalDebt: 4.8,
          codeSmells: 18,
          duplications: 5,
          lastAnalysis: new Date(Date.now() - 900000).toISOString()
        }
      ];

      const mockIssues: QualityIssue[] = [
        {
          id: '1',
          type: 'error',
          severity: 'high',
          message: 'Unused variable detected',
          file: 'AdminDashboard.tsx',
          line: 45,
          rule: 'no-unused-vars',
          category: 'Best Practices',
          timestamp: new Date(Date.now() - 300000).toISOString()
        },
        {
          id: '2',
          type: 'warning',
          severity: 'medium',
          message: 'Function is too complex',
          file: 'SuperAdminDashboard.tsx',
          line: 123,
          rule: 'complexity',
          category: 'Code Smells',
          timestamp: new Date(Date.now() - 600000).toISOString()
        },
        {
          id: '3',
          type: 'info',
          severity: 'low',
          message: 'Consider using const instead of let',
          file: 'ChillSetterDashboard.tsx',
          line: 67,
          rule: 'prefer-const',
          category: 'Best Practices',
          timestamp: new Date(Date.now() - 900000).toISOString()
        }
      ];

      setQualityMetrics(mockMetrics);
      setIssues(mockIssues);
    } catch (error) {
      console.error('Error fetching quality data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-brand-red/20 text-brand-red';
      case 'medium': return 'bg-brand-blue-light/20 text-brand-blue-light';
      case 'low': return 'bg-scarf-green/20 text-scarf-green';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'error': return 'text-brand-red';
      case 'warning': return 'text-brand-blue-light';
      case 'info': return 'text-scarf-green';
      default: return 'text-gray-600';
    }
  };

  const getMaintainabilityColor = (index: number) => {
    if (index >= 90) return 'text-scarf-green';
    if (index >= 80) return 'text-brand-blue-light';
    if (index >= 70) return 'text-brand-red';
    return 'text-brand-red';
  };

  const filteredIssues = selectedCategory === 'all'
    ? issues
    : issues.filter(issue => issue.category === selectedCategory);

  const categories = ['all', ...Array.from(new Set(issues.map(issue => issue.category)))];

  return (
    <div className="space-y-6">
      {/* Quality Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        {qualityMetrics.map((project) => (
          <div key={project.project} className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">{project.project}</h3>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Issues:</span>
                <span className="text-brand-red font-bold">{project.totalIssues}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Errors:</span>
                <span className="text-brand-red font-bold">{project.errors}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Warnings:</span>
                <span className="text-brand-blue-light font-bold">{project.warnings}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Maintainability:</span>
                <span className={`font-bold ${getMaintainabilityColor(project.maintainabilityIndex)}`}>
                  {project.maintainabilityIndex}/100
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Technical Debt:</span>
                <span className="text-brand-red font-bold">{project.technicalDebt}h</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Code Smells:</span>
                <span className="text-brand-blue-light font-bold">{project.codeSmells}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-gray-500 text-sm">
                Last analysis: {new Date(project.lastAnalysis).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Quality Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={fetchQualityData}
            className="bg-brand-red hover:bg-brand-red/90 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Refresh Analysis
          </button>
          <button
            onClick={() => console.log('Run quality analysis')}
            className="bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Run Analysis
          </button>
          <button
            onClick={() => console.log('Generate quality report')}
            className="bg-scarf-green hover:bg-scarf-green/90 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Generate Report
          </button>
        </div>
      </div>

      {/* Issues Filter */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Quality Issues</h3>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-frost-white border border-ice-teal-light rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-brand-blue"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-gray-800">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Type</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Severity</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Message</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">File</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Line</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Category</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Rule</th>
              </tr>
            </thead>
            <tbody>
              {filteredIssues.map((issue) => (
                <tr key={issue.id} className="border-b border-gray-100">
                  <td className="py-3 px-4">
                    <span className={`font-bold ${getTypeColor(issue.type)}`}>
                      {issue.type.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(issue.severity)}`}>
                      {issue.severity}
                    </span>
                  </td>
                  <td className="py-3 px-4">{issue.message}</td>
                  <td className="py-3 px-4 font-mono text-sm">{issue.file}</td>
                  <td className="py-3 px-4">{issue.line}</td>
                  <td className="py-3 px-4 text-sm">{issue.category}</td>
                  <td className="py-3 px-4 font-mono text-xs">{issue.rule}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quality Trends */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Quality Trends</h3>
        <div className="grid md:grid-cols-3 gap-6">
          {qualityMetrics.map((project) => (
            <div key={project.project} className="text-center">
              <h4 className="text-deep-navy font-bold mb-2">{project.project}</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Issues:</span>
                  <span className="text-brand-red">{project.totalIssues}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Maintainability:</span>
                  <span className={getMaintainabilityColor(project.maintainabilityIndex)}>
                    {project.maintainabilityIndex}/100
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Technical Debt:</span>
                  <span className="text-brand-red">{project.technicalDebt}h</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QualityComponent;
